import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { apiFetch } from '../api/client';
import { authHeaders, devGetMe, ServiceInfo } from '../api/dev';
import Spinner from '../components/Spinner';
import GeoMapModal from '../components/GeoMapModal';
import OnboardingModal from '../components/OnboardingModal';
import AccessPolicyCard from '../components/AccessPolicyCard';
import WebhookConfigSection from '../components/WebhookConfigSection';

type Rule = { parameter: string; operator: string; value?: number; values?: number[]; unit?: string; leadHours?: number; locationIndex?: number };
// Store lat/lon as strings in the UI so users can type '-', backspace, etc.
type Location = { lat: string; lon: string; name?: string };
type Config = { webhookUrl: string; rules: Rule[]; locations: Location[]; enabled?: boolean; leadHours?: number };

export default function DevForecastPush() {
  // Me/meta
  const [services, setServices] = useState<string[]>([]);
  const [serviceInfo, setServiceInfo] = useState<ServiceInfo[]>([]);
  const [policy, setPolicy] = useState<any>(null);
  const allowed = useMemo(() => new Set(services), [services]);
  const svcId = 'forecast-push';
  const meta = useMemo(() => serviceInfo.find(s => s.id === svcId), [serviceInfo]);
  const title = meta?.name || 'Forecast Push Notifications';
  const description = meta?.description || 'Webhook alerts based on forecast conditions.';

  const [config, setConfig] = useState<Config>({ enabled: false, webhookUrl: '', leadHours: 2, rules: [], locations: [] });
  const [params, setParams] = useState<Array<{ id: string; name: string; unit?: string; ops: string[] }>>([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingMe, setLoadingMe] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);
  const [copyOpen, setCopyOpen] = useState<{ index: number }|null>(null);
  const [copyTargets, setCopyTargets] = useState<Set<number>>(new Set());
  const [ruleFilterIdx, setRuleFilterIdx] = useState<string>('all'); // 'all' | 'any' | numeric index as string
  const [hasSecret, setHasSecret] = useState<boolean>(false);
  const [generatedSecret, setGeneratedSecret] = useState<string>('');
  const [hasApiKey, setHasApiKey] = useState<boolean>(false);
  const [apiKeyId, setApiKeyId] = useState<string>('');
  const [devKeys, setDevKeys] = useState<Array<{ keyId: string; status: string }>>([]);
  const [revealedKey, setRevealedKey] = useState<string>('');
  const [showKey, setShowKey] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [simLoading, setSimLoading] = useState<boolean>(false);
  const [sim, setSim] = useState<any>(null);
  // Keep a snapshot of last-saved numeric locations to avoid accidental loss when saving non-location settings
  const [savedLocations, setSavedLocations] = useState<Array<{ lat: number; lon: number; name?: string }>>([]);
  // Per-card messages
  const [okWebhook, setOkWebhook] = useState('');
  const [errWebhook, setErrWebhook] = useState('');
  const [okLocations, setOkLocations] = useState('');
  const [errLocations, setErrLocations] = useState('');
  const [okRules, setOkRules] = useState('');
  const [errRules, setErrRules] = useState('');
  const [showRulesMap, setShowRulesMap] = useState(false);
  // Rule numeric inputs (strings so users can type '-' etc.) indexed by rule index
  const [ruleInputs, setRuleInputs] = useState<Record<number, { value?: string; min?: string; max?: string }>>({});
  const getRuleInput = (i: number) => ruleInputs[i] || {};
  const setRuleInput = (i: number, patch: Partial<{ value?: string; min?: string; max?: string }>) => {
    setRuleInputs((prev) => ({ ...prev, [i]: { ...(prev[i] || {}), ...patch } }));
  };

  // Location validation helpers
  function parseNum(s: string): number { return parseFloat(String(s).trim()); }
  const locationIssues = useMemo(() => {
    return (config.locations || []).map((l) => {
      const latStr = (l.lat || '').trim();
      const lonStr = (l.lon || '').trim();
      const latNum = parseNum(latStr);
      const lonNum = parseNum(lonStr);
      const latOk = latStr !== '' && Number.isFinite(latNum) && latNum >= -90 && latNum <= 90;
      const lonOk = lonStr !== '' && Number.isFinite(lonNum) && lonNum >= -180 && lonNum <= 180;
      return { latOk, lonOk, ok: latOk && lonOk };
    });
  }, [config.locations]);
  const hasInvalidLocations = useMemo(() => (locationIssues || []).some(x => !x.ok), [locationIssues]);

  // Route-driven sub-tabs and guides
  const loc = useLocation();
  const tab = useMemo<'about' | 'access' | 'webhook' | 'webhook-test' | 'rules' | 'locations'>(() => {
    const p = String((loc as any)?.pathname || '');
    if (p.includes('/forecast-push/access')) return 'access';
    if (p.includes('/forecast-push/webhook-test')) return 'webhook-test';
    if (p.includes('/forecast-push/webhook')) return 'webhook';
    if (p.includes('/forecast-push/rules')) return 'rules';
    if (p.includes('/forecast-push/locations')) return 'locations';
    return 'about';
  }, [loc?.pathname]);
  const [showAccessGuide, setShowAccessGuide] = useState(false);
  const [showWebhookGuide, setShowWebhookGuide] = useState(false);
  const [showRulesGuide, setShowRulesGuide] = useState(false);
  const [showLocationsGuide, setShowLocationsGuide] = useState(false);
  const [showWebhookTestGuide, setShowWebhookTestGuide] = useState(false);

  useEffect(() => {
    (async () => {
      setLoadingPage(true);
      try {
        // Load meta/me
        setLoadingMe(true);
        try {
          const me = await devGetMe();
          setServices(me.services || []);
          setServiceInfo(me.serviceInfo || []);
          setPolicy(me.policy || null);
        } finally { setLoadingMe(false); }

        // Load parameters + existing config
        const p = await apiFetch('/dev/forecast-push/parameters', { headers: { ...authHeaders() } }).then(r => r.json()).catch(()=>({parameters:[]}));
        setParams(p.parameters || []);
        const c = await apiFetch('/dev/forecast-push/config', { headers: { ...authHeaders() } }).then(r => r.json()).catch(()=>({config:{}}));
        const cfg = c.config || {};
        setHasSecret(!!c.hasSecret);
        const loadedApiKeyId = String(c.apiKeyId || cfg.apiKeyId || '');
        setApiKeyId(loadedApiKeyId);
        setHasApiKey(Boolean(c.hasApiKey || loadedApiKeyId));
        try {
          const r = await apiFetch('/dev/keys', { headers: { ...authHeaders() } });
          const j = await r.json().catch(() => ({}));
          if (r.ok && Array.isArray(j.items)) setDevKeys(j.items);
        } catch {}
        const defaultLead = (typeof cfg.leadHours === 'number' && cfg.leadHours > 0) ? cfg.leadHours : 2;
        const rules: Rule[] = (cfg.rules || []).map((r: any) => ({
          ...r,
          leadHours: (typeof r.leadHours === 'number' && r.leadHours > 0) ? r.leadHours : defaultLead,
          unit: r.unit || (Array.isArray(p.parameters) ? (p.parameters.find((pp: any) => pp.id === r.parameter)?.unit) : undefined)
        }));
        const locs: Location[] = (cfg.locations || []).map((l: any) => ({ lat: String(l.lat ?? ''), lon: String(l.lon ?? ''), name: l.name }));
        setConfig({ enabled: !!cfg.enabled, webhookUrl: cfg.webhookUrl || '', leadHours: defaultLead, rules, locations: locs });
        try {
          const numericLocs = (cfg.locations || []).filter((l: any) => Number.isFinite(l?.lat) && Number.isFinite(l?.lon)).map((l: any) => ({ lat: Number(l.lat), lon: Number(l.lon), name: l.name }));
          setSavedLocations(numericLocs);
        } catch {}
      } catch (e: any) { setError(e?.message || String(e)); }
      finally { setLoadingPage(false); }
    })();
  }, []);

  function parseLocationsToNumeric(list: Location[]): Array<{ lat: number; lon: number; name?: string }> {
    return (list || [])
      .map((l) => {
        const lat = parseFloat(String(l.lat).trim());
        const lon = parseFloat(String(l.lon).trim());
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null as any;
        const name = (l.name || '').trim();
        return { lat, lon, name: name || undefined } as { lat: number; lon: number; name?: string };
      })
      .filter(Boolean) as Array<{ lat: number; lon: number; name?: string }>;
  }

  function buildMergedRules(): Rule[] {
    const toNum = (s: string | undefined) => {
      if (s === undefined) return undefined;
      const n = Number(String(s).trim());
      return Number.isFinite(n) ? n : undefined;
    };
    return (config.rules || []).map((r, i) => {
      const inp = getRuleInput(i);
      if (r.operator === 'between') {
        const a = inp.min !== undefined ? toNum(inp.min) : r.values?.[0];
        const b = inp.max !== undefined ? toNum(inp.max) : r.values?.[1];
        const vals: number[] = [] as any;
        if (typeof a === 'number') vals[0] = a;
        if (typeof b === 'number') vals[1] = b;
        const out: any = { ...r };
        if (vals.length) out.values = vals;
        delete out.value;
        return out as Rule;
      }
      const v = inp.value !== undefined ? toNum(inp.value) : r.value;
      const out: any = { ...r };
      out.value = typeof v === 'number' ? v : undefined;
      delete out.values;
      return out as Rule;
    });
  }

  function buildCleanConfig(useSavedLocsIfInvalid = false) {
    const useSaved = useSavedLocsIfInvalid && hasInvalidLocations && savedLocations && savedLocations.length >= 0;
    const numericLocs = useSaved ? savedLocations : parseLocationsToNumeric(config.locations || []);
    const rulesMerged = buildMergedRules();
    return {
      ...config,
      rules: rulesMerged,
      // Preserve associated API key id and secret flag for admin visibility
      apiKeyId: apiKeyId || (config as any).apiKeyId,
      secretSet: hasSecret || (config as any).secretSet === true,
      locations: numericLocs,
    };
  }

  async function save() {
    setError(''); setOk(''); setLoading(true);
    try {
      if (hasInvalidLocations) {
        throw new Error('Please fix latitude/longitude values (lat: -90..90, lon: -180..180).');
      }
      // Convert location lat/lon strings to numbers; drop invalid rows
      const cleanConfig = buildCleanConfig(false);
      const res = await apiFetch('/dev/forecast-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: cleanConfig }) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setOk('Saved');
      setSavedLocations(cleanConfig.locations || []);
    } catch (e: any) { setError(e?.message || String(e)); } finally { setLoading(false); }
  }

  function addRule() {
    const defaultLead = (typeof config.leadHours === 'number' && config.leadHours > 0) ? config.leadHours : 2;
    setConfig({ ...config, rules: [...config.rules, { parameter: params[0]?.id || 'temperature', operator: '>', value: 10, leadHours: defaultLead }] });
    setRuleInputs({});
  }
  function updateRule(i: number, patch: Partial<Rule>) {
    const next = [...config.rules];
    next[i] = { ...next[i], ...patch } as Rule;
    setConfig({ ...config, rules: next });
  }
  function removeRule(i: number) { setConfig({ ...config, rules: config.rules.filter((_,idx)=>idx!==i) }); setRuleInputs({}); }

  function addLocation() {
    setConfig({ ...config, locations: [...config.locations, { lat: '', lon: '', name: '' }] });
  }
  function updateLocation(i: number, patch: Partial<Location>) {
    const next = [...config.locations];
    next[i] = { ...next[i], ...patch } as Location;
    setConfig({ ...config, locations: next });
  }
  function removeLocation(i: number) { setConfig({ ...config, locations: config.locations.filter((_,idx)=>idx!==i) }); }

  // Compute geo (Access Policy): specific override or global
  const geo = useMemo(() => {
    const bySvc = (policy?.geoByService || {})[svcId];
    return bySvc || (policy?.geo || { bboxes: [], circles: [] });
  }, [policy]);

  return (
    <div>
      <h2>{title}</h2>
      {(loadingPage || loading) && <Spinner label="Loading..." />}
      <div aria-hidden={(loadingPage || loading)} style={{ display: (loadingPage || loading) ? 'none' : undefined }}>
      {!allowed.has(svcId) && !loadingMe && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div className="muted">You do not have access to this service.</div>
        </div>
      )}

      {allowed.has(svcId) && (
        <>
          {tab === 'about' && (
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0 }}>About</h3>
            </div>
          )}
          <div className="card" style={{ marginBottom: 12, display: (tab === 'about') ? undefined : 'none' }}>
            <h3 style={{ marginTop: 0 }}>About this service</h3>
            <div className="muted" style={{ marginTop: 4 }}>{description}</div>
            <div style={{ marginTop: 8, lineHeight: 1.6 }}>
              <div style={{ fontWeight: 600, marginBottom: 4 }}>How it works</div>
              <ul style={{ marginTop: 0, paddingLeft: 18 }}>
                <li>Evaluates your configured rules (parameter/operator/threshold) against forecast data per location.</li>
                <li>Sends one signed webhook per tenant every hour with a summary of evaluations (triggered true/false) for each rule and location.</li>
                <li>Signing: HMAC SHA-256 using your tenant secret. Headers include <code>X-AFW-Event</code>, <code>X-AFW-Delivery-Id</code>, <code>X-AFW-Timestamp</code>, <code>X-AFW-Signature</code>.</li>
                <li>Usage metering: each hourly summary consumes one unit per configured location (for example, 3 locations → 3 units).</li>
              </ul>
            </div>
            <div className="muted" style={{ marginTop: 8 }}>
              Tip: Every page includes a <strong>Show Guide</strong> button to help you get oriented and understand more.
            </div>
          </div>
          {tab === 'access' && (
            <div>
              <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <h3 style={{ margin: 0 }}>Access</h3>
                <button className="btn warning" onClick={() => setShowAccessGuide(true)}>Show Guide</button>
              </div>
              <AccessPolicyCard policy={policy} svcId={svcId} />
            </div>
          )}

      {tab === 'webhook' && (
        <>
          <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <h3 style={{ margin: 0 }}>Webhook</h3>
          <button className="btn warning" onClick={() => setShowWebhookGuide(true)}>Show Guide</button>
        </div>
        <WebhookConfigSection
            svc="forecast-push"
            webhookUrl={config.webhookUrl}
            setWebhookUrl={(s) => setConfig({ ...config, webhookUrl: s })}
            apiKeyId={apiKeyId}
            setApiKeyId={setApiKeyId}
            hasSecret={hasSecret}
            setHasSecret={setHasSecret}
            onSaveConfig={async () => {
              const cleanConfig = buildCleanConfig(false);
              const res = await apiFetch('/dev/forecast-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: cleanConfig }) });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
            }}
          />
        </>
      )}
        </>
      )}

      <div className="card" style={{ maxWidth: 900, padding: 16, marginBottom: 12, display: 'none' }}>
        <h3 style={{ marginTop: 0 }}>Webhook Configuration</h3>
        <div className="form-grid">
          {/* Webhook URL */}
          <div className="form-row">
            <div className="form-label">Webhook URL</div>
            <div className="form-value">
              <input className="txt" style={{ flex: 1, minWidth: 260 }} value={config.webhookUrl} onChange={e => setConfig({ ...config, webhookUrl: e.target.value })} placeholder="https://example.com/webhook" />
            </div>
          </div>
          {/* Signing Secret */}
          <div className="form-row">
            <div className="form-label">Signing Secret</div>
            <div className="form-value">
              {hasSecret ? (
                <>
                  <span className="muted">Secret is set.</span>
                  <button className="btn info" onClick={async () => {
                    try {
                      setGeneratedSecret('');
                      const r = await apiFetch('/dev/forecast-push/secret', { method: 'POST', headers: { ...authHeaders() } });
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                      setHasSecret(true);
                      setGeneratedSecret(j.secret || '');
                    } catch (e: any) { setErrWebhook(e?.message || String(e)); }
                  }}>Regenerate Secret</button>
                </>
              ) : (
                <>
                  <span className="muted">No secret set. Generate one to sign webhooks.</span>
                  <button className="btn info" onClick={async () => {
                    try {
                      const r = await apiFetch('/dev/forecast-push/secret', { method: 'POST', headers: { ...authHeaders() } });
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                      setHasSecret(true);
                      setGeneratedSecret(j.secret || '');
                    } catch (e: any) { setErrWebhook(e?.message || String(e)); }
                  }}>Generate Secret</button>
                </>
              )}
            </div>
          </div>
          {/* Forecast API Key */}
          <div className="form-row">
            <div className="form-label">Forecast API Key</div>
            <div className="form-value">
              {hasApiKey ? (
                <>
                  <span className="muted">Using key: <code>{apiKeyId}</code></span>
                  <button className="btn info" onClick={async () => {
                    try {
                      setShowKey(true);
                      const r = await apiFetch(`/dev/keys/${encodeURIComponent(apiKeyId)}`, { headers: { ...authHeaders() } });
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                      setRevealedKey(j.key || '');
                    } catch (e: any) { setErrWebhook(e?.message || String(e)); }
                  }}>Reveal</button>
                  <button className="btn neutral" onClick={() => { setHasApiKey(false); setShowKey(false); setRevealedKey(''); }}>Change…</button>
                </>
              ) : (
                <>
                  <select className="txt" style={{ minWidth: 240 }} value={apiKeyId} onChange={e => { setApiKeyId(e.target.value); setRevealedKey(''); setShowKey(false); }}>
                    <option value="">Select a key…</option>
                    {devKeys.map(k => (
                      <option key={k.keyId} value={k.keyId}>{k.keyId} ({k.status})</option>
                    ))}
                  </select>
                  <button className="btn info" disabled={!apiKeyId} onClick={async () => {
                    try {
                      setShowKey(true);
                      const r = await apiFetch(`/dev/keys/${encodeURIComponent(apiKeyId)}`, { headers: { ...authHeaders() } });
                      const j = await r.json().catch(() => ({}));
                      if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                      setRevealedKey(j.key || '');
                    } catch (e: any) { setErrWebhook(e?.message || String(e)); }
                  }}>Reveal Value</button>
                  {/* Saving is handled by the main "Save Webhook" action; this per-field save button is removed. */}
                </>
              )}
            </div>
          </div>
        </div>
        {generatedSecret && (
          <div className="card" style={{ padding: 10, marginBottom: 8 }}>
            <div className="muted" style={{ marginBottom: 4 }}>Copy this secret now; it will be shown only once.</div>
            <code>{generatedSecret}</code>
          </div>
        )}
        
        {showKey && revealedKey && (
          <div className="card" style={{ padding: 10, marginBottom: 8 }}>
            <div className="muted" style={{ marginBottom: 4 }}>Key value (hide when done)</div>
            <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
              <code style={{ userSelect: 'all', overflowX: 'auto' }}>{revealedKey}</code>
              <button
                className="btn secondary"
                title="Copy"
                aria-label="Copy API key"
                onClick={async () => {
                  try {
                    if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(revealedKey);
                    else {
                      const ta = document.createElement('textarea');
                      ta.value = revealedKey; document.body.appendChild(ta); ta.select(); document.execCommand('copy'); ta.remove();
                    }
                    setCopied(true); setTimeout(() => setCopied(false), 1500);
                  } catch {}
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
              </button>
              {copied && <span className="muted" style={{ color: 'lightgreen' }}>Copied!</span>}
            </div>
            <div className="row" style={{ gap: 8, marginTop: 6 }}>
              <button className="btn neutral" onClick={() => { setShowKey(false); setRevealedKey(''); }}>Hide</button>
            </div>
          </div>
        )}
        {errWebhook && <div style={{ color: 'crimson', marginTop: 8 }}>{errWebhook}</div>}
        {okWebhook && <div style={{ color: 'lightgreen', marginTop: 8 }}>{okWebhook}</div>}
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn" onClick={async () => {
            setErrWebhook(''); setOkWebhook(''); setLoading(true);
            try {
              // If an API key is selected and a value is revealed, persist it to SSM first
              if (apiKeyId && revealedKey) {
                const rKey = await apiFetch('/dev/forecast-push/api-key', { method: 'POST', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ keyId: apiKeyId, key: revealedKey }) });
                if (!rKey.ok) {
                  const j = await rKey.json().catch(() => ({}));
                  throw new Error(j?.error?.message || `HTTP ${rKey.status}`);
                }
                setHasApiKey(true);
              }
              const cleanConfig = buildCleanConfig(true); // preserve locations if invalid
              const res = await apiFetch('/dev/forecast-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: cleanConfig }) });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              setOkWebhook('Webhook configuration saved');
            } catch (e: any) { setErrWebhook(e?.message || String(e)); }
            finally { setLoading(false); }
          }}>Save Webhook</button>
        </div>
      </div>

      {/* Locations Card */}
      {tab === 'locations' && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Locations</h3>
          <button className="btn warning" onClick={() => setShowLocationsGuide(true)}>Show Guide</button>
        </div>
      )}
      <div className="card" style={{ maxWidth: 900, padding: 16, marginBottom: 12, display: (tab==='locations') ? undefined : 'none' }}>
        {tab === 'locations' && (
        <>
        
        {config.locations.length === 0 && <div className="muted" style={{ marginBottom: 8 }}>No locations</div>}
        {config.locations.map((l, i) => (
          <div key={i} className="row" style={{ gap: 8, marginBottom: 8 }}>
            <input className="txt" placeholder="Name (optional)" value={l.name || ''} onChange={e => updateLocation(i, { name: e.target.value })} />
            <input className="txt" type="text" inputMode="decimal" placeholder="latitude" value={l.lat || ''} onChange={e => updateLocation(i, { lat: e.target.value })} style={{ borderColor: locationIssues[i]?.latOk === false ? 'crimson' : undefined }} />
            <input className="txt" type="text" inputMode="decimal" placeholder="longitude" value={l.lon || ''} onChange={e => updateLocation(i, { lon: e.target.value })} style={{ borderColor: locationIssues[i]?.lonOk === false ? 'crimson' : undefined }} />
            <button className="btn danger" onClick={() => removeLocation(i)}>Remove</button>
          </div>
        ))}
        {(config.locations.length > 0) && hasInvalidLocations && (
          <div className="muted" style={{ color: 'crimson', marginTop: -4, marginBottom: 8 }}>
            Latitude must be between -90 and 90; Longitude between -180 and 180.
          </div>
        )}
        {errLocations && <div style={{ color: 'crimson', marginTop: 8 }}>{errLocations}</div>}
        {okLocations && <div style={{ color: 'lightgreen', marginTop: 8 }}>{okLocations}</div>}
        <div className="row" style={{ gap: 8, marginTop: 8 }}>
          <button className="btn info" onClick={addLocation}>Add Location</button>
          <button className="btn" disabled={hasInvalidLocations || loading} onClick={async () => {
            setErrLocations(''); setOkLocations(''); setLoading(true);
            try {
              if (hasInvalidLocations) throw new Error('Please fix latitude/longitude values');
              const cleanConfig = buildCleanConfig(false);
              const res = await apiFetch('/dev/forecast-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: cleanConfig }) });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              setSavedLocations(cleanConfig.locations || []);
              setOkLocations('Locations saved');
            } catch (e: any) { setErrLocations(e?.message || String(e)); }
            finally { setLoading(false); }
          }}>Save Locations</button>
        </div>
        </>
        )}
      </div>

      {/* Show Guides (Forecast-specific) */}
      <OnboardingModal
        open={showAccessGuide}
        onClose={() => setShowAccessGuide(false)}
        title="Access Tab Guide"
        steps={[
          {
            title: 'About Access',
            body: (
              <>
                <div>- Shows your geographic access policy for Forecast Push.</div>
                <div>- Only requests within this policy are eligible.</div>
                <div>- Notifications are sent only when your configured <strong>Locations</strong> and <strong>Rules</strong> overlap these allowed areas.</div>
                <div>- Regions/Locations outside the policy are ignored even if rules match.</div>
              </>
            ),
          },
          {
            title: 'Map View',
            body: (
              <>
                <div>- Use Map View (where available) to visualize coverage and check overlap with Locations/Regions.</div>
              </>
            ),
          },
        ]}
      />
      <OnboardingModal
        open={showWebhookGuide}
        onClose={() => setShowWebhookGuide(false)}
        title="Webhook Tab Guide"
        steps={[
          { title: 'Webhook URL', body: (<><div>- HTTPS endpoint to receive signed JSON POSTs from Forecast Push.</div><div>- Keep the URL stable; avoid query strings for easier rotation and testing.</div></>) },
          { title: 'Signing Secret', body: (<><div>- Generate/Regenerate the HMAC secret; it is stored securely.</div><div>- Verify <code>X-AFW-Signature</code> and <code>X-AFW-Timestamp</code> freshness.</div></>) },
          { title: 'API Key', body: (<><div>- Select a key and save; used to send notifications every hour to the configured webhook URL.</div><div>- Keys can be rotated; select the new key and Save to update.</div></>) },
          { title: 'Response Expectations', body: (<><div>- Your endpoint should return a 2xx code on success. 4xx/5xx failures are not retried; attempts are logged and considered processed.</div></>) },
        ]}
      />
      <OnboardingModal
        open={showLocationsGuide}
        onClose={() => setShowLocationsGuide(false)}
        title="Locations Guide"
        steps={[
          { title: 'Basics', body: (<><div>- Add the locations to evaluate for rules.</div><div>- Latitude [-90..90], Longitude [-180..180].</div></>) },
          { title: 'Saving', body: (<><div>- Fix invalid coordinates before saving.</div></>) },
        ]}
      />
      <OnboardingModal
        open={showRulesGuide}
        onClose={() => setShowRulesGuide(false)}
        title="Rules Guide"
        steps={[
          { title: 'Define Rules', body: (<><div>- Choose a parameter, operator and value (or range).</div><div>- Target “Any location” or a specific one.</div></>) },
          { title: 'Map View', body: (<><div>- Preview rules per location on the map (hover markers to see details).</div></>) },
        ]}
      />
      <OnboardingModal
        open={showWebhookTestGuide}
        onClose={() => setShowWebhookTestGuide(false)}
        title="Webhook Test Guide"
        steps={[
          { title: 'What it does', body: (<><div>- Runs a one‑off simulated delivery: fetches forecast, evaluates rules and posts a signed test payload.</div></>) },
          { title: 'Prerequisites', body: (<><div>- Configure Webhook URL and generate a Signing Secret first.</div></>) },
          { title: 'Reading results', body: (<><div>- Inspect headers, payload and response to verify integration. Aim for 2xx responses.</div></>) },
        ]}
      />

      {/* Rules Card */}
      {tab === 'rules' && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Rules</h3>
          <button className="btn warning" onClick={() => setShowRulesGuide(true)}>Show Guide</button>
        </div>
      )}
      <div className="card" style={{ maxWidth: 900, padding: 16, marginBottom: 12, display: (tab==='rules') ? undefined : 'none' }}>
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ marginTop: 0, marginBottom: 0 }}>Rules</h3>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <button className="btn info" onClick={() => setShowRulesMap(true)}>Map View</button>
          </div>
        </div>
        {config.rules.length === 0 && <div className="muted" style={{ marginBottom: 8 }}>No rules</div>}
        {/* Rules toolbar */}
        <div className="row" style={{ gap: 8, margin: '6px 0 10px 0', justifyContent: 'flex-start', alignItems: 'center' }}>
          <div className="row" style={{ gap: 8, alignItems: 'center' }}>
            <div className="muted">Filter by location:</div>
            <select className="txt" style={{ minWidth: 200 }} value={ruleFilterIdx} onChange={e => setRuleFilterIdx(e.target.value)}>
              <option value="all">All rules</option>
              <option value="any">Any location</option>
              {config.locations.map((loc, idx) => (
                <option key={idx} value={String(idx)}>{loc.name ? `${loc.name} (${loc.lat},${loc.lon})` : `${loc.lat},${loc.lon}`}</option>
              ))}
            </select>
          </div>
        </div>
        {(config.rules || []).filter((r) => {
          if (ruleFilterIdx === 'all') return true;
          if (ruleFilterIdx === 'any') return r.locationIndex === undefined || r.locationIndex === null;
          const idx = Number(ruleFilterIdx);
          return r.locationIndex === idx;
        }).map((r, i) => (
          <div key={i} style={{ marginBottom: 10 }}>
            {/* Rule header */}
            <div className="row" style={{ gap: 8, marginBottom: 6, alignItems: 'center', justifyContent: 'space-between' }}>
              <div className="row" style={{ gap: 8, alignItems: 'center' }}>
                <span style={{
                  background: 'rgba(59,130,246,0.15)',
                  border: '1px solid rgba(59,130,246,0.4)',
                  padding: '2px 8px',
                  borderRadius: 9999,
                  fontSize: 12,
                  whiteSpace: 'nowrap'
                }}>
                  {(() => {
                    const idx = (r.locationIndex ?? -1) as number;
                    const loc = (idx >= 0 && idx < config.locations.length) ? config.locations[idx] : null;
                    if (!loc) return 'Any location';
                    return loc.name ? loc.name : `${loc.lat}, ${loc.lon}`;
                  })()}
                </span>
                <div className="muted" style={{ fontSize: 12 }}>Rule</div>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn secondary" onClick={() => { const next = [...config.rules]; next.push({ ...r }); setConfig({ ...config, rules: next }); setRuleInputs({}); }}>Duplicate</button>
                <button className="btn secondary" onClick={() => { setCopyOpen({ index: (config.rules.indexOf(r)) }); setCopyTargets(new Set()); }}>Copy to locations…</button>
                <button className="btn danger" onClick={() => removeRule(config.rules.indexOf(r))}>Remove</button>
              </div>
            </div>
            {/* Rule body row 1: parameter/operator/value */}
            <div className="row" style={{ gap: 8, marginBottom: 6, alignItems: 'center' }}>
              <select className="txt" value={r.parameter} onChange={e => {
                const pid = e.target.value;
                const u = (params.find(p => p.id === pid)?.unit) || r.unit;
                updateRule(i, { parameter: pid, unit: u });
              }}>
                {params.map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
              </select>
              <select className="txt" value={r.operator} onChange={e => updateRule(i, { operator: e.target.value })}>
                {(params.find(p => p.id === r.parameter)?.ops || ['>','>=','<','<=','==','!=','between']).map(op => (<option key={op} value={op}>{op}</option>))}
              </select>
              {r.operator === 'between' ? (
                <>
                  <input className="txt" type="text" inputMode="decimal" placeholder="min" value={(getRuleInput(i).min ?? (r.values?.[0] ?? '') as any) as string} onChange={e => setRuleInput(i, { min: e.target.value })} />
                  <input className="txt" type="text" inputMode="decimal" placeholder="max" value={(getRuleInput(i).max ?? (r.values?.[1] ?? '') as any) as string} onChange={e => setRuleInput(i, { max: e.target.value })} />
                </>
              ) : (
                <input className="txt" type="text" inputMode="decimal" placeholder="value" value={(getRuleInput(i).value ?? (r.value ?? '') as any) as string} onChange={e => setRuleInput(i, { value: e.target.value })} />
              )}
              <span className="muted" style={{ fontSize: 12 }}>
                {r.unit || (params.find(p => p.id === r.parameter)?.unit) || ''}
              </span>
            </div>
            {/* Rule body row 2: location target */}
            <div className="row" style={{ gap: 8, marginBottom: 6 }}>
              <label className="muted" style={{ minWidth: 120 }}>Target location</label>
              <select className="txt" value={r.locationIndex ?? ''} onChange={e => updateRule(i, { locationIndex: e.target.value === '' ? undefined : Number(e.target.value) })}>
                <option value="">Any location</option>
                {config.locations.map((loc, idx) => (
                  <option key={idx} value={idx}>{loc.name ? `${loc.name} (${loc.lat},${loc.lon})` : `${loc.lat},${loc.lon}`}</option>
                ))}
              </select>
            </div>
            <div className="row" style={{ gap: 8, marginLeft: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span className="muted">Notify this many hours before the forecast time
                  <span title="We evaluate the forecast at the hour N hours from now. A notification is sent N hours beforehand if the condition will be met at that forecasted hour." style={{ marginLeft: 6, cursor: 'help', borderBottom: '1px dotted rgba(255,255,255,0.4)' }}>ⓘ</span>
                </span>
                <input
                  className="txt"
                  style={{ width: 140 }}
                  type="number"
                  min={1}
                  max={240}
                  placeholder="e.g. 2"
                  value={r.leadHours ?? (config.leadHours ?? 2)}
                  onChange={e => updateRule(i, { leadHours: Number(e.target.value) })}
                />
              </label>
            </div>
          </div>
        ))}
        <button className="btn success" onClick={addRule}>Add Rule</button>

        {errRules && <div style={{ color: 'crimson', marginTop: 8 }}>{errRules}</div>}
        {okRules && <div style={{ color: 'lightgreen', marginTop: 8 }}>{okRules}</div>}
        <div className="row" style={{ gap: 8, marginTop: 12 }}>
          <button className="btn" disabled={loading} onClick={async () => {
            setErrRules(''); setOkRules(''); setLoading(true);
            try {
              // Preserve locations if invalid while saving rules
              const cleanConfig = buildCleanConfig(true);
              const res = await apiFetch('/dev/forecast-push/config', { method: 'PUT', headers: { 'Content-Type': 'application/json', ...authHeaders() }, body: JSON.stringify({ config: cleanConfig }) });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              setOkRules('Rules saved');
            } catch (e: any) { setErrRules(e?.message || String(e)); }
            finally { setLoading(false); }
          }}>Save Rules</button>
        </div>
      </div>

      {/* Webhook Test Heading */}
      {tab === 'webhook-test' && (
        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <h3 style={{ margin: 0 }}>Webhook Test</h3>
          <button className="btn warning" onClick={() => setShowWebhookTestGuide(true)}>Show Guide</button>
        </div>
      )}
      {/* Test Webhook Card */}
      <div className="card" style={{ maxWidth: 900, padding: 16, display: (tab==='webhook-test') ? undefined : 'none' }}>
        <div className="card" style={{ padding: 10, marginBottom: 10 }}>
          <div className="muted" style={{ marginBottom: 8 }}>Runs a one-off simulated delivery: fetches forecast, evaluates all rules, and POSTs a signed test payload to your webhook URL.</div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn info" disabled={!hasSecret || !config.webhookUrl || simLoading} onClick={async () => {
              try {
                setError(''); setOk(''); setSim(null); setSimLoading(true);
                const r = await apiFetch('/dev/forecast-push/simulate', { method: 'POST', headers: { ...authHeaders() } });
                const j = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(j?.error?.message || `HTTP ${r.status}`);
                setSim(j);
              } catch (e: any) { setError(e?.message || String(e)); }
              finally { setSimLoading(false); }
            }}>{simLoading ? 'Running...' : (hasSecret ? 'Run Test' : 'Generate Secret First')}</button>
            {!config.webhookUrl && <span className="muted" style={{ color: 'crimson' }}>Webhook URL is required.</span>}
          </div>
          {sim && (
            <div style={{ marginTop: 10 }}>
              <div className="muted">HTTP {sim.status} • {sim.ok ? 'Success' : 'Failed'}</div>
              <div style={{ marginTop: 6 }}>
                <h4 style={{ margin: '6px 0' }}>Headers Sent</h4>
                <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{JSON.stringify(sim.request?.headers || {}, null, 2)}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                <h4 style={{ margin: '6px 0' }}>Payload</h4>
                <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{JSON.stringify(sim.request?.body || {}, null, 2)}</code>
              </div>
              <div style={{ marginTop: 6 }}>
                <h4 style={{ margin: '6px 0' }}>Webhook Response</h4>
                <code style={{ display: 'block', whiteSpace: 'pre-wrap' }}>{sim.response?.body || ''}</code>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      

      {/* Access Policy Map moved into AccessPolicyCard */}
      <GeoMapModal open={tab==='rules' && showRulesMap} onClose={() => setShowRulesMap(false)} points={(config.locations || []).map((l, i) => {
        const lat = parseNum(l.lat);
        const lon = parseNum(l.lon);
        if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null as any;
        const rules = buildMergedRules().filter((r) => (r.locationIndex === undefined || r.locationIndex === null) || r.locationIndex === i);
        const title = l.name ? l.name : `${l.lat}, ${l.lon}`;
        const latStr = Number.isFinite(lat) ? lat.toFixed(4) : String(lat);
        const lonStr = Number.isFinite(lon) ? lon.toFixed(4) : String(lon);
        const rows = rules.map((r) => {
          const op = r.operator;
          const pv = (r.operator === 'between') ? `${(r.values?.[0] ?? '')}..${(r.values?.[1] ?? '')}` : (r.value ?? '');
          const u = (r.unit || (params.find(p => p.id === r.parameter)?.unit) || '');
          const lead = r.leadHours ?? (config.leadHours ?? 2);
          return `${r.parameter} ${op} ${pv}${u ? ' ' + u : ''} @ ${lead}h`;
        }).slice(0, 12);
        const tooltip = `<div><strong>${title}</strong><div style=\"margin-top:2px;color:#94a3b8;\">(${latStr}, ${lonStr})</div>${rows.length ? '<div style=\"margin-top:4px;\"><em>Rules</em><br/>' + rows.map(x=>x.replace(/</g,'&lt;')).join('<br/>') + '</div>' : '<div style=\"margin-top:4px;\"><em>No rules</em></div>'}</div>`;
        return { lat, lon, label: title, tooltipHtml: tooltip };
      }).filter(Boolean)} title="Rules Map (hover markers to see rules)" />
      {copyOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'grid', placeItems: 'center', zIndex: 50 }}>
          <div className="card" style={{ width: 520, maxWidth: '90vw' }}>
            <h3 style={{ marginTop: 0 }}>Copy Rule to Locations</h3>
            <div className="muted" style={{ marginBottom: 8 }}>Select locations to receive a copy of this rule.</div>
            <div style={{ maxHeight: 260, overflow: 'auto', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 8, padding: 8, marginBottom: 8 }}>
              {config.locations.length === 0 && <div className="muted">No locations configured</div>}
              {config.locations.map((loc, idx) => (
                <label key={idx} className="row" style={{ justifyContent: 'space-between', marginBottom: 6 }}>
                  <div>{loc.name || `${loc.lat}, ${loc.lon}`}</div>
                  <input type="checkbox" checked={copyTargets.has(idx)} onChange={e => { const next = new Set(copyTargets); if (e.target.checked) next.add(idx); else next.delete(idx); setCopyTargets(next); }} />
                </label>
              ))}
            </div>
            <div className="row" style={{ gap: 8, justifyContent: 'space-between' }}>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn secondary" onClick={() => setCopyTargets(new Set())}>Clear</button>
                <button className="btn secondary" onClick={() => setCopyTargets(new Set(config.locations.map((_,i)=>i)))}>Select All</button>
              </div>
              <div className="row" style={{ gap: 6 }}>
                <button className="btn" onClick={() => {
                  if (!copyOpen) return;
                  const src = config.rules[copyOpen.index];
                  const newRules = [...config.rules];
                  copyTargets.forEach((idx) => {
                    newRules.push({ ...src, locationIndex: idx });
                  });
                  setConfig({ ...config, rules: newRules });
                  setRuleInputs({});
                  setCopyOpen(null);
                }} disabled={copyTargets.size === 0}>Copy</button>
                <button className="btn secondary" onClick={() => setCopyOpen(null)}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
